// StatusInput.js
import { useRef, useState, useCallback, useEffect } from "react";
import { useVoiceRecorder } from "../components/recording";
import { detectMediaType } from "./MediaUtils";

/* ─── Icons (same set as ChatInput, trimmed to what StatusInput needs) ──── */
const AttachIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
    <path d="M16.5 6v11.5c0 2.21-1.79 4-4 4s-4-1.79-4-4V5c0-1.38 1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5v10.5c0 .55-.45 1-1 1s-1-.45-1-1V6H10v9.5c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5V5c0-2.21-1.79-4-4-4S7 2.79 7 5v12.5c0 3.04 2.46 5.5 5.5 5.5s5.5-2.46 5.5-5.5V6h-1.5z"/>
  </svg>
);
const MicIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
    <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z"/>
  </svg>
);
const SendIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
  </svg>
);
const DeleteIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zm2.46-7.12l1.41-1.41L12 12.59l2.12-2.12 1.41 1.41L13.41 14l2.12 2.12-1.41 1.41L12 15.41l-2.12 2.12-1.41-1.41L10.59 14l-2.13-2.12zM15.5 4l-1-1h-5l-1 1H5v2h14V4z"/>
  </svg>
);
const PauseIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
  </svg>
);
const PlayIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
    <path d="M8 5v14l11-7z"/>
  </svg>
);

/* ─── Waveform (live recording) — same math as ChatInput's WaveformBars ── */
function WaveformBars({ seconds, paused }) {
  return (
    <div className="flex items-center gap-[3px] flex-1 overflow-hidden">
      <span className="text-sm font-medium tabular-nums text-red-500 mr-2 shrink-0">
        {String(Math.floor(seconds / 60)).padStart(2, "0")}:
        {String(seconds % 60).padStart(2, "0")}
      </span>
      {Array.from({ length: 22 }).map((_, i) => (
        <div
          key={i}
          className="w-[3px] rounded-full shrink-0"
          style={{
            height: `${10 + Math.sin(i * 1.3) * 7}px`,
            backgroundColor: paused ? "#94a3b8" : "#22c55e",
            animation: paused ? "none" : "waveBar 0.6s ease-in-out infinite alternate",
            animationDelay: `${(i * 40) % 300}ms`,
            minHeight: "4px",
            maxHeight: "22px",
          }}
        />
      ))}
    </div>
  );
}

/* ─── Paused preview player — same behavior as ChatInput's version ─────── */
function PausedPreviewPlayer({ url }) {
  const audioRef = useRef(null);
  const rafRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    setPlaying(false);
    setCurrent(0);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, [url]);

  useEffect(() => () => {
    cancelAnimationFrame(rafRef.current);
    audioRef.current?.pause();
  }, []);

  const tick = useCallback(() => {
    if (!audioRef.current) return;
    setCurrent(audioRef.current.currentTime);
    if (!audioRef.current.paused) rafRef.current = requestAnimationFrame(tick);
  }, []);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      a.play().then(() => {
        setPlaying(true);
        rafRef.current = requestAnimationFrame(tick);
      });
    } else {
      a.pause();
      setPlaying(false);
      cancelAnimationFrame(rafRef.current);
    }
  };

  const progress = duration > 0 ? current / duration : 0;
  const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

  const scrub = (e) => {
    const a = audioRef.current;
    if (!a || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.max(0, Math.min(e.clientX - rect.left, rect.width)) / rect.width;
    a.currentTime = pct * duration;
    setCurrent(a.currentTime);
  };

  return (
    <div className="flex items-center gap-2 flex-1 min-w-0">
      <audio
        ref={audioRef}
        src={url}
        preload="metadata"
        onLoadedMetadata={() =>
          setDuration(isFinite(audioRef.current?.duration) ? audioRef.current.duration : 0)
        }
        onEnded={() => {
          setPlaying(false);
          setCurrent(0);
          if (audioRef.current) audioRef.current.currentTime = 0;
          cancelAnimationFrame(rafRef.current);
        }}
        onPause={() => setPlaying(false)}
      />
      <button
        type="button"
        onClick={toggle}
        className="flex items-center justify-center w-8 h-8 rounded-full bg-green-500 hover:bg-green-600 text-white shrink-0 active:scale-90 transition-all"
        aria-label={playing ? "Pause preview" : "Play preview"}
      >
        {playing ? <PauseIcon /> : <PlayIcon />}
      </button>
      <div
        className="flex-1 flex items-center gap-[2px] h-7 cursor-pointer select-none"
        onClick={scrub}
        role="slider"
        aria-valuemin={0}
        aria-valuemax={duration}
        aria-valuenow={current}
      >
        {Array.from({ length: 26 }).map((_, i) => {
          const h = 0.15 + (((i * 7 + 13) % 17) / 17) * 0.85;
          const active = i / 26 <= progress;
          return (
            <div
              key={i}
              className="flex-1 rounded-full transition-colors duration-75"
              style={{
                height: Math.max(3, h * 24),
                backgroundColor: active ? "#25D366" : "rgba(255,255,255,0.25)",
                minWidth: 2,
              }}
            />
          );
        })}
      </div>
      <span className="text-[11px] font-medium tabular-nums text-white/70 shrink-0">
        {playing || current > 0 ? fmt(current) : fmt(duration)}
      </span>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════════════
   StatusInput — the reply bar used inside StatusViewer.
   Mirrors ChatInput.js's normal/recording/paused rows 1:1, minus:
     - edit mode
     - @mentions
     - schedule-send
     - emoji picker
   Manages its own text + voice-recording state; only reports finished
   actions upward via onSendText / onSendMedia.
═════════════════════════════════════════════════════════════════════════ */
export function StatusInput({
  placeholder = "Reply…",
  disabled = false,
  disabledMessage = "You can't reply to this status",
  sending = false,
  autoFocus = false,
  allowEmptySend = false, // when true, the send button stays active even with no text (e.g. posting media with no caption)
  onSendText,     // (text: string) => void | Promise<void>
  onSendMedia,    // (file: File, meta: object) => void | Promise<void>  — optional
}) {
  const [text, setText] = useState("");

  const textInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const containerRef = useRef(null);

  /* ── same recorder hook, same onComplete contract as ChatWindow's use ── */
  const {
    isRecording,
    isPaused,
    recordingSeconds,
    previewUrl,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    cancelRecording,
  } = useVoiceRecorder({
    onComplete: useCallback(
      async (file, url) => {
        if (!file) return;
        await onSendMedia?.(file, {
          type: "audio",
          mimeType: file.type || "audio/webm",
          fileName: file.name,
          fileSize: file.size,
          isVoice: true,
          previewUrl: url,
        });
      },
      [onSendMedia]
    ),
  });

  const inVoice = isRecording || isPaused;
  const canSend = !disabled && !sending && (allowEmptySend || text.trim().length > 0);

  const handleSend = useCallback(async () => {
    if (!canSend) return;

    const trimmed = text.trim();

    setText("");

    await onSendText?.(trimmed);

  }, [canSend, text, onSendText]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSend();
    }
  };

    const MAX_ATTACHMENTS = 30;

  const handleFileSelect = (e) => {
    if (disabled) return;
    let files = Array.from(e.target.files || []);
    if (!files.length) return;

    if (files.length > MAX_ATTACHMENTS) {
      files = files.slice(0, MAX_ATTACHMENTS);
    }

    // One onSendMedia call per file — same one-message-per-attachment
    // convention ChatInput.js uses for multi-media sends.
    files.forEach((file) => {
      const type = detectMediaType(file);
      onSendMedia?.(file, {
        type,
        mimeType: file.type || "",
        fileName: file.name,
        fileSize: file.size,
        previewUrl: URL.createObjectURL(file),
      });
    });

    e.target.value = "";
  };

  return (
    <div
      ref={containerRef}
      className="relative flex flex-col gap-0 w-full"
      onClick={(e) => e.stopPropagation()}
    >
     
           <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        accept="image/*,video/*"
        onChange={handleFileSelect}
        disabled={disabled}
      />

      {/* ── Live recording row ── */}
      {isRecording && !disabled && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={cancelRecording}
            className="flex items-center justify-center w-10 h-10 shrink-0 rounded-full text-white/70 hover:bg-white/10 active:scale-90 transition-all touch-manipulation"
            aria-label="Cancel recording"
          >
            <DeleteIcon />
          </button>

          <div className="flex-1 flex items-center px-3 py-2 rounded-full min-w-0 bg-white/10">
            <WaveformBars seconds={recordingSeconds ?? 0} paused={false} />
          </div>

          <button
            type="button"
            onClick={pauseRecording}
            className="flex items-center justify-center w-9 h-9 rounded-full shrink-0 bg-white/15 text-white hover:bg-white/25 active:scale-90 transition-all touch-manipulation"
            aria-label="Pause recording"
          >
            <PauseIcon />
          </button>

          <button
            type="button"
            onClick={stopRecording}
            className="flex items-center justify-center w-10 h-10 rounded-full shrink-0 bg-[#25D366] hover:bg-[#1fa855] active:scale-90 text-white shadow-md transition-all touch-manipulation"
            style={{ animation: "statusMicPulse 1.5s ease-in-out infinite" }}
            aria-label="Send voice reply"
          >
            <SendIcon />
          </button>
        </div>
      )}

      {/* ── Paused recording row ── */}
      {isPaused && !isRecording && !disabled && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={cancelRecording}
            className="flex items-center justify-center w-10 h-10 shrink-0 rounded-full text-white/70 hover:bg-white/10 active:scale-90 transition-all touch-manipulation"
            aria-label="Cancel recording"
          >
            <DeleteIcon />
          </button>

          <div className="flex-1 flex items-center px-3 py-2 rounded-full min-w-0 bg-white/10">
            {previewUrl ? (
              <PausedPreviewPlayer url={previewUrl} />
            ) : (
              <WaveformBars seconds={recordingSeconds ?? 0} paused />
            )}
          </div>

          <button
            type="button"
            onClick={resumeRecording}
            className="flex items-center justify-center w-9 h-9 rounded-full shrink-0 bg-white/15 text-white hover:bg-white/25 active:scale-90 transition-all touch-manipulation"
            aria-label="Resume recording"
          >
            <MicIcon />
          </button>

          <button
            type="button"
            onClick={stopRecording}
            className="flex items-center justify-center w-10 h-10 rounded-full shrink-0 bg-[#25D366] hover:bg-[#1fa855] active:scale-90 text-white shadow-md transition-all touch-manipulation"
            aria-label="Send voice reply"
          >
            <SendIcon />
          </button>
        </div>
      )}

      {/* ── Normal text row ── */}
      {!inVoice && (
        <div className="relative flex items-center gap-2">
          <input
            ref={textInputRef}
            type="text"
            autoFocus={autoFocus}
            disabled={disabled}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={disabled ? disabledMessage : placeholder}
            className={`flex-1 px-4 py-2 rounded-full text-sm outline-none min-w-0 bg-white/10 text-white placeholder-white/50 focus:ring-2 focus:ring-[#25D366] ${
              disabled ? "opacity-50 cursor-not-allowed" : ""
            }`}
          />

          <button
            type="button"
            disabled={disabled}
            onClick={() => { if (!disabled) fileInputRef.current?.click(); }}
            className={`flex items-center justify-center w-9 h-9 shrink-0 rounded-full transition-colors touch-manipulation ${
              disabled ? "opacity-40 cursor-not-allowed text-white/40" : "text-white/80 hover:text-white active:scale-90"
            }`}
            aria-label="Attach media"
          >
            <AttachIcon />
          </button>

          <button
            type="button"
            disabled={disabled || sending}
            onClick={() => { if (canSend) handleSend(); else startRecording?.(); }}
            className={`flex items-center justify-center w-10 h-10 rounded-full text-white shrink-0 transition-all touch-manipulation active:scale-90 ${
              disabled || sending ? "bg-white/20 cursor-not-allowed" : "bg-[#25D366] hover:bg-[#1fa855]"
            }`}
            aria-label={canSend ? "Send reply" : "Record voice reply"}
          >
            {canSend ? <SendIcon /> : <MicIcon />}
          </button>
        </div>
      )}

      <style>{`
        @keyframes waveBar { from { transform: scaleY(0.35); } to { transform: scaleY(1); } }
        @keyframes statusMicPulse {
          0%,100% { box-shadow: 0 0 0 0 rgba(37,211,102,.5); }
          50%      { box-shadow: 0 0 0 8px rgba(37,211,102,0); }
        }
      `}</style>
    </div>
  );
}

export default StatusInput;